import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { PolicyEnforcement } from '@experiences/interfaces';
import {
    SpacingToken,
    UiCircularProgress,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import {
    Box,
    Chip,
} from '@mui/material';
import { FontVariantToken } from '@uipath/apollo-core';
import { ApAlertBar } from '@uipath/portal-shell-react';
import React, {
    lazy,
    Suspense,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import { WizardStepHeader } from '../WizardStepLayout';
import { PolicyDefinitionCard } from './common/PolicyDefinitionCard';
import { PolicyEditDrawer } from './common/PolicyEditDrawer';
import useGovernancePolicyCreatePolicyDefinitionStepViewModel from './GovernancePolicyCreatePolicyDefinitionStepViewModel';

const PolicyResourceRule = lazy(() => import('./rules/resource/PolicyResourceRule'));
const PolicyAutomationRule = lazy(() => import('./rules/automation/PolicyAutomationRule'));
const PolicyAccessDecisionRule = lazy(() => import('./rules/access-decision/PolicyAccessDecisionRule'));

type DrawerSection = 'resources' | 'automations' | 'accessDecision';

const TAG_CHIP_SX = {
    borderColor: 'var(--color-foreground-brand)',
    color: 'var(--color-foreground-brand)',
} as const;

const CHIP_WRAP_SX = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
} as const;

interface RuleChipDisplayProps {
    display: {
        typeChips: string[];
        tags: string[];
        tagOperatorShort: string;
    };
    showTags: boolean;
}

const RuleChipDisplay: React.FC<RuleChipDisplayProps> = ({
    display, showTags,
}) => (
    <UiStack
        direction="column"
        gap={SpacingToken.S}>
        <Box sx={CHIP_WRAP_SX}>
            {display.typeChips.map((chip, i) => (
                <Chip
                    key={i}
                    label={chip}
                    size="small" />
            ))}
        </Box>
        {showTags && display.tags.length > 0 && (
            <Box sx={CHIP_WRAP_SX}>
                <Chip
                    label={display.tagOperatorShort}
                    size="small" />
                {display.tags.map((tag, i) => (
                    <Chip
                        key={i}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={TAG_CHIP_SX}
                    />
                ))}
            </Box>
        )}
    </UiStack>
);

export const GovernancePolicyCreatePolicyDefinitionStep: React.FC = () => {
    const { formatMessage: translate } = useIntl();
    const EnableGovernanceAccessDecisionEdit = useFeatureFlagValue(Features.EnableGovernanceAccessDecisionEdit.name);

    const [ activeSection, setActiveSection ] = useState<DrawerSection | null>(null);

    const {
        resourcesDisplay,
        automationsDisplay,
        accessDecisionDisplay,
        enforcement,
        isResourcesEmpty,
        isAutomationsEmpty,
        isLoadingNames,
        isOrganizationScope,
    } = useGovernancePolicyCreatePolicyDefinitionStepViewModel();

    const drawerTitles: Record<DrawerSection, string> = {
        resources: translate({ id: 'CLIENT_GOVERNANCE_RESOURCE_RULE_EDIT_TITLE' }),
        automations: translate({ id: 'CLIENT_GOVERNANCE_AUTOMATION_RULE_EDIT_TITLE' }),
        accessDecision: translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACCESS_DECISION_EDIT_TITLE' }),
    };

    const closeDrawer = () => setActiveSection(null);

    if (isLoadingNames) {
        return (
            <Box
                sx={{
                    padding: '24px',
                    height: '100%',
                }}
            >
                <UiStack
                    direction="column"
                    justify="center"
                    align="center"
                    h="100%"
                    data-cy="governance-policy-definition-loading"
                >
                    <UiCircularProgress />
                </UiStack>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                padding: '24px',
            }}>
                <UiStack
                    direction="column"
                    justify="between"
                    data-cy="governance-policy-create-rules-step"
                    h="100%"
                >
                    <UiStack
                        direction="column"
                        gap={SpacingToken.L}
                    >
                        <WizardStepHeader
                            title={translate({ id: 'CLIENT_GOVERNANCE_STEP_POLICY_DEFINITION_TITLE' })}
                            description={translate({ id: 'CLIENT_GOVERNANCE_STEP_POLICY_DEFINITION_DESCRIPTION' })}
                        />
                        {(isResourcesEmpty || isAutomationsEmpty) && (
                            <ApAlertBar
                                status="warning"
                                cancelable={false}
                                data-cy="governance-policy-definition-validation-warning"
                                style={{
                                    width: '100%',
                                    borderRadius: '8px',
                                }}
                            >
                                {translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_AND_AUTOMATIONS_MUST_BE_SELECTED' })}
                            </ApAlertBar>
                        )}
                        <UiStack
                            direction="row"
                            gap={SpacingToken.L}
                            data-cy="governance-policy-definition-cards"
                        >
                            <PolicyDefinitionCard
                                title={translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES' })}
                                description={translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_RESOURCES_DESCRIPTION' })}
                                onEdit={() => setActiveSection('resources')}
                                editDataCy="governance-policy-definition-card-resources-edit"
                                cardDataCy="governance-policy-definition-card-resources"
                            >
                                {resourcesDisplay ? (
                                    <RuleChipDisplay
                                        display={resourcesDisplay}
                                        showTags={!isOrganizationScope}
                                    />
                                ) : (
                                    <UiText variant={FontVariantToken.fontSizeS}>
                                        {translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_NO_RESOURCES_SELECTED' })}
                                    </UiText>
                                )}
                            </PolicyDefinitionCard>
                            <PolicyDefinitionCard
                                title={translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS' })}
                                description={translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_AUTOMATIONS_DESCRIPTION' })}
                                onEdit={() => setActiveSection('automations')}
                                editDataCy="governance-policy-definition-card-automations-edit"
                                cardDataCy="governance-policy-definition-card-automations"
                            >
                                {automationsDisplay ? (
                                    <RuleChipDisplay
                                        display={automationsDisplay}
                                        showTags={!isOrganizationScope}
                                    />
                                ) : (
                                    <UiText variant={FontVariantToken.fontSizeS}>
                                        {translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_NO_AUTOMATIONS_SELECTED' })}
                                    </UiText>
                                )}
                            </PolicyDefinitionCard>
                            <PolicyDefinitionCard
                                title={translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACCESS_DECISION' })}
                                description={translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_ACCESS_DECISION_DESCRIPTION' })}
                                onEdit={() => setActiveSection('accessDecision')}
                                editDataCy="governance-policy-definition-card-access-decision-edit"
                                cardDataCy="governance-policy-definition-card-access-decision"
                                showEditButton={EnableGovernanceAccessDecisionEdit}
                            >
                                <UiStack
                                    direction="column"
                                    gap={SpacingToken.XS}
                                    mt={SpacingToken.XS}
                                >
                                    {accessDecisionDisplay ? (
                                        <UiText
                                            variant={FontVariantToken.fontSizeMBold}
                                            style={enforcement === PolicyEnforcement.Allow ? { color: 'var(--color-success-icon)' } : undefined}
                                        >
                                            {accessDecisionDisplay}
                                        </UiText>
                                    ) : (
                                        <UiText variant={FontVariantToken.fontSizeS}>
                                            {translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_NO_ACCESS_DECISION_SELECTED' })}
                                        </UiText>
                                    )}
                                </UiStack>
                            </PolicyDefinitionCard>
                        </UiStack>
                    </UiStack>
                </UiStack>
            </Box>

            {activeSection && (
                <PolicyEditDrawer
                    open
                    title={drawerTitles[activeSection]}
                    onClose={closeDrawer}
                >
                    <Suspense fallback={<UiCircularProgress />}>
                        {activeSection === 'resources' && (
                            <PolicyResourceRule
                                onDone={closeDrawer}
                                onCancel={closeDrawer}
                            />
                        )}
                        {activeSection === 'automations' && (
                            <PolicyAutomationRule
                                onDone={closeDrawer}
                                onCancel={closeDrawer}
                            />
                        )}
                        {activeSection === 'accessDecision' && (
                            <PolicyAccessDecisionRule
                                onDone={closeDrawer}
                                onCancel={closeDrawer}
                            />
                        )}
                    </Suspense>
                </PolicyEditDrawer>
            )}
        </>
    );
};
